import { AiSupportCard, AiUtilityHandoff } from 'src/components/shared/aiAssistant';
import React, { FC, useState } from 'react';
import styled from 'styled-components';
import { AiCreditsNotice, AiHeading } from 'src/components/shared/aiAssistant';
import Sparkles from 'src/components/widgets/svg/sparkles';
import { T } from 'src/shared/helpers';
import theme from 'src/shared/theme';
import { AiGenerateButton, AiOutlineButton, AIGeneratingIndicator } from '../../../../widgets/ai';
import { Dialog } from '../../../../widgets/dialog';
import { LargeMessageDialogWindow } from './printAreas';

// A side of the product plan produced by the AI prompt parser: display name + English text-to-image prompt.
export interface AiProductPlanSide {
    name: string;
    imagePrompt: string;
}

// The full product structure parsed from the user's natural-language prompt (new products only).
export interface AiProductPlan {
    supportRequest?: import('src/shared/aiSupport').EmailSupportRequest | null;
    handoffPrompt?: string | null;
    productName: string;
    sides: AiProductPlanSide[];
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 20px;
    min-height: 260px;
`;

// Shared Zakeke AI heading (same as the other AI dialogs), spaced from the description below.
const DialogHeading = styled(AiHeading)`
    margin-bottom: 8px;
`;

const Description = styled.p`
    margin: 0;
    color: #57534e;
    font-size: 14px;
    line-height: 1.5;
`;

const Field = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const Textarea = styled.textarea`
    min-height: 96px;
    padding: 8px 10px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    color: #263238;
    background: #fff;
    outline: none;
    resize: vertical;

    &:focus {
        border-color: ${theme.ai.solid};
    }
`;

const SummaryBox = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 12px 14px;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    background: #f8fafc;
`;

const SummaryRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 13px;
    color: #57534e;

    strong {
        color: #1e293b;
    }
`;

const SideChips = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
`;

const SideChip = styled.span`
    padding: 3px 10px;
    border-radius: 999px;
    background: #ede9fe;
    color: #6b21a8;
    font-size: 12px;
`;

const ErrorText = styled.div`
    color: #dc2626;
    font-size: 12px;
`;

const Footer = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding-top: 8px;
`;

// AI-assisted product creation dialog (new products only), styled like the other AI generation surfaces:
// gradient title, no banner, AI buttons and an inline generating bar. Steps swap the body inside the same
// modal (describe → analyze → confirm → generate); the modal closes only via its X. The heavy lifting
// (parse + generate + assemble) is provided by the caller through `parsePrompt`/`generate`.
export const AiProductCreationDialog: FC<{
    parsePrompt: (prompt: string) => Promise<AiProductPlan | null>;
    // `onDone` is invoked when the generation has actually finished (or the flow terminated early, e.g. no
    // credits), so the dialog closes only after the product is created — not when the preflight returns.
    generate: (plan: AiProductPlan, onProgress: (done: number, total: number, label: string) => void, onDone: () => void) => void;
    onClose: () => void;
}> = ({ parsePrompt, generate, onClose }) => {
    const [phase, setPhase] = useState<'prompt' | 'analyzing' | 'confirm' | 'generating'>('prompt');
    const [prompt, setPrompt] = useState('');
    const [plan, setPlan] = useState<AiProductPlan | null>(null);
    const [error, setError] = useState<string | null>(null);

    const handleContinue = async () => {
        setError(null);
        setPhase('analyzing');
        try {
            const result = await parsePrompt(prompt.trim());
            if (result?.supportRequest || result?.handoffPrompt) {
                setPlan(result);
                setPhase('prompt');
                return;
            }
            if (!result || result.sides.length === 0) {
                setError(T._('The product could not be interpreted. Try rephrasing your description.', 'Admin'));
                setPhase('prompt');
                return;
            }
            setPlan(result);
            setPhase('confirm');
        } catch {
            setError(T._('The product could not be interpreted. Try rephrasing your description.', 'Admin'));
            setPhase('prompt');
        }
    };

    const handleGenerate = () => {
        if (!plan) return;
        setPhase('generating');
        // Stay on the generating step; the dialog closes via onDone when the generation actually finishes.
        // (Handles addon first-activation, where generation runs asynchronously after the addon is activated.)
        generate(plan, () => {}, onClose);
    };

    return (
        <Dialog windowDecorator={LargeMessageDialogWindow} onClose={onClose}>
            <Container>
                <div>
                    <DialogHeading caption={T._('Create your product with AI', 'Admin')} />
                    {plan?.supportRequest && <AiSupportCard request={plan.supportRequest} />}
                    {plan?.handoffPrompt && <AiUtilityHandoff prompt={plan.handoffPrompt} onClose={onClose} />}
                    {phase === 'prompt' && (
                        <Description>
                            {T._(
                                "Describe your product, including its type, color, and any printable sides (such as the front and back of a T-shirt). AI will generate images and print areas for each side. Don't describe product variants here, you'll configure them in the next step.",
                                'Admin'
                            )}
                        </Description>
                    )}
                </div>

                {phase === 'prompt' && (
                    <>
                        <Field>
                            <Textarea
                                autoFocus
                                value={prompt}
                                placeholder={T._("e.g. Create a black T-shirt with two sides: 'Front' and 'Back'", 'Admin')}
                                onChange={(e) => setPrompt(e.currentTarget.value)}
                            />
                            {error && <ErrorText>{error}</ErrorText>}
                        </Field>
                        <AiCreditsNotice />
                        <Footer>
                            <AiGenerateButton type='button' disabled={!prompt.trim()} onClick={handleContinue}>
                                <Sparkles />
                                {T._('Continue', 'Admin')}
                            </AiGenerateButton>
                        </Footer>
                    </>
                )}

                {phase === 'analyzing' && (
                    <AIGeneratingIndicator
                        phrases={[
                            T._('Reading your description...', 'Admin'),
                            T._('Identifying the product and its sides...', 'Admin'),
                            T._('Preparing the plan...', 'Admin')
                        ]}
                    />
                )}

                {phase === 'confirm' && plan && (
                    <>
                        <SummaryBox>
                            <SummaryRow>
                                <span>{T._('Product', 'Admin')}</span>
                                <strong>{plan.productName}</strong>
                            </SummaryRow>
                            <SummaryRow>
                                <span>{T._('Sides', 'Admin')}</span>
                                <strong>{plan.sides.length}</strong>
                            </SummaryRow>
                            <SummaryRow>
                                <span>{T._('Print areas', 'Admin')}</span>
                                <strong>{plan.sides.length}</strong>
                            </SummaryRow>
                            <SideChips>
                                {plan.sides.map((side, index) => (
                                    <SideChip key={`${side.name}-${index}`}>{side.name}</SideChip>
                                ))}
                            </SideChips>
                        </SummaryBox>
                        <Footer>
                            <AiOutlineButton type='button' onClick={() => setPhase('prompt')}>
                                {T._('Edit description', 'Admin')}
                            </AiOutlineButton>
                            <AiGenerateButton type='button' onClick={handleGenerate}>
                                <Sparkles />
                                {T._('Generate', 'Admin')}
                            </AiGenerateButton>
                        </Footer>
                    </>
                )}

                {phase === 'generating' && (
                    <AIGeneratingIndicator
                        phrases={[
                            T._('Generating your product with AI...', 'Admin'),
                            T._('Creating the side images...', 'Admin'),
                            T._('Detecting the print areas...', 'Admin'),
                            T._('Almost ready...', 'Admin')
                        ]}
                    />
                )}
            </Container>
        </Dialog>
    );
};
